<template>
  <div class="container-fluid main-container">
    <div class="row mb-2">
      <div class="col-xl-4 d-flex justify-content-between align-items-end flex-wrap">
        <div class="ms-1">
          <h2 class="title-blue">{{ t("MANAGE_PROFILE") }}</h2>
          <h4>{{ t(role) }}</h4>
        </div>
        <Badge v-if="isArchived" severity="warn" size="large" class="mb-3 ms-1">
          {{ t("ARCHIVED") }}
        </Badge>
      </div>
    </div>

    <div class="row">
      <div class="col-xl-4 mb-3">
        <profile-form show-unconfirmed-email-alert @after-save="afterProfileFormSave"/>
      </div>

      <div class="col-xl-8 mb-3">
        <Tabs :value="tabsList.find(tab => tab.show)?.key">
          <TabList>
            <template v-for="tab in tabsList">
              <Tab :value="tab.key" v-if="tab.show">{{ tab.title }}</Tab>
            </template>
          </TabList>

          <TabPanels>
            <TabPanel v-if="route.params.id && !auth.hasRole(Role.TRAINEE)" value="0">
              <PaginatedTable id="paginated-accountHistoryLogs"
                              :rows-per-page="perPage"
                              :on-page-change="getAccountHistoryLogs"
                              :fields="logFields"
                              :small="true"
                              ref="accountHistoryLogsRef"
              >
                <template v-slot:cell(created_at)="{ item }">
                  {{ dateFormat(item.createdAt) + " " + timeFormat(item.createdAt) }}
                </template>
                <template v-slot:cell(trainingGroup)="{ item }">
                <span v-if="item.group">
                  <a href="#" @click.prevent="navToGroup(item.group.id)">{{ item.group.title }}</a>
                  <span v-if="item.group && item.destinationGroup" class="mx-2">&rarr;</span>
                  <span v-if="item.destinationGroup">
                    <a href="#" @click.prevent="navToGroup(item.destinationGroup.id)">{{ item.destinationGroup.title }}</a>
                  </span>
                </span>
                </template>
                <template v-slot:cell(training)="{ item }">
                  <span v-if="item.group">{{ item.group.training.translations[locale] }}</span>
                </template>
                <template v-slot:cell(type)="{ item }">
                  <Badge :severity="historyInfoSeverity(item)" size="large">{{ t(item.type) }}</Badge>
                </template>
              </PaginatedTable>
            </TabPanel>

            <TabPanel v-if="trainings" value="1">
              <DataTable
                  :value="trainings"
                  striped-rows
                  size="small"
                  paginator
                  :rowHover="true"
                  :alwaysShowPaginator="false"
                  :removableSort="true"
                  :rows="perPage"
              >
                <template #empty>{{ t('NO_RESULTS') }}</template>
                <Column field="startTime" :header="t('START')" sortField="startTimeIso" sortable/>
                <Column field="title" :header="t('GROUP')" sortable/>
                <Column field="moduleName" :header="t('MODULE')" sortable/>
                <Column field="badgeType" :header="t('RESULT')" sortable>
                  <template #body="{ data }">
                    <Badge v-if="data.badgeType === 'ended'" severity="success" size="large">{{ t("ENDED") }}</Badge>
                    <Badge v-if="data.badgeType === 'upcoming'" severity="info" size="large">{{ t("UPCOMING") }}</Badge>
                    <Badge v-if="data.badgeType === 'ongoing'" severity="secondary" size="large">{{ t("ONGOING") }}</Badge>
                  </template>
                </Column>
                <Column v-if="hasRoleToViewGroup()">
                  <template #body="{ data }">
                    <Button size="small" :label="t('VIEW')" @click="navToGroup(data.id)"/>
                  </template>
                </Column>
              </DataTable>
            </TabPanel>

            <TabPanel v-if="modules" value="2">
              <DataTable
                  :value="modules"
                  striped-rows
                  paginator
                  size="small"
                  :rowHover="true"
                  :alwaysShowPaginator="false"
                  :removableSort="true"
                  :rows="perPage"
              >
                <template #empty>{{ t('NO_RESULTS') }}</template>
                <Column field="code" :header="t('CODE_NAME')" sortable/>
                <Column field="moduleName" :header="t('NAME')" sortable/>
                <Column field="moduleType" :header="t('MODULE_TYPE')" sortable/>
              </DataTable>
            </TabPanel>

            <TabPanel v-if="schools" value="3">
              <DataTable
                  :value="schools"
                  striped-rows
                  paginator
                  size="small"
                  :rowHover="true"
                  :alwaysShowPaginator="false"
                  :removableSort="true"
                  :rows="perPage"
              >
                <template #empty>{{ t('NO_RESULTS') }}</template>
                <Column field="registryCode" :header="t('SCHOOL_CODE')" sortable/>
                <Column field="businessName" :header="t('SCHOOL_NAME')" sortable/>
                <Column>
                  <template #body="{ data }">
                    <div class="text-center">
                      <router-link :to="{ name: 'ManageSchool', params: { id: data.id, school: data } }">
                        <Button size="small" :label="t('VIEW')"/>
                      </router-link>
                    </div>
                  </template>
                </Column>
              </DataTable>
            </TabPanel>

            <TabPanel v-if="role === Role.TRAINEE" value="4">
              <PaginatedTable id="paginated-accountAttendances"
                              :rows-per-page="perPage"
                              :on-page-change="getAttendance"
                              :fields="attendanceFields"
                              :small="true"
              >
                <template v-slot:cell(trainingEvent.startTime)="{ item }">
                  <span v-if="item.trainingEvent">{{ dateFormat(item.trainingEvent.startTime) }}</span>
                  <span v-else>{{ t("TRANSFERRED") }}</span>
                </template>
                <template v-slot:cell(status)="{ item }">
                  <span>{{ t(item.status) }}</span>
                </template>
              </PaginatedTable>
            </TabPanel>

            <TabPanel v-if="adminTrainers" value="5">
              <DataTable :value="adminTrainers"
                         striped-rows
                         paginator
                         size="small"
                         :rowHover="true"
                         :alwaysShowPaginator="false"
                         :removableSort="true"
                         :rows="perPage"
              >
                <template #empty>{{ t('NO_RESULTS') }}</template>
                <Column field="fullName" :header="t('NAME')" sortable/>
                <Column field="ssn" :header="t('SSN')" sortable/>
                <Column field="email" :header="t('EMAIL')" sortable/>
                <Column field="phone" :header="t('PHONE')" sortable/>
              </DataTable>
            </TabPanel>

            <TabPanel v-if="doShowInstitutionDiplomasTab" value="6">
              <institution-diplomas :account-id="route.params.id || auth.getAcc()" :rows-per-page="perPage"/>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </div>
</template>

<script setup>
import ProfileForm from "@/views/components/forms/ProfileForm.vue";
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";
import useTimeFormat from "@/mixins/timeFormatMixin2";
import moment from "moment-timezone";
import {computed, onMounted, ref} from "vue";
import {injectAxios, injectAuth} from "@/util/inject";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {Role} from "@/auth/oauth";
import PaginatedTable from "@/views/components/PaginatedTable.vue";
import {useToast} from "@/util/toastUtils";
import InstitutionDiplomas from "@/views/components/InstitutionDiplomas.vue";

const axios = injectAxios();
const route = useRoute();
const router = useRouter();
const {t, locale} = useI18n();
const toast = useToast();
const auth = injectAuth();
const {dateFormat, dateFormatIso} = useDateFormat();
const {timeFormat} = useTimeFormat();

const trainings = ref(null);
const schools = ref(null);
const adminTrainers = ref(null);
const perPage = ref(15);
const role = ref("");
const modules = ref(null);
const accountHistoryLogsRef = ref(null);
const isArchived = ref(false);
const id = route.params.id;

onMounted(async () => {
  await fetchIsArchivedById();
  await getRole();

  if (role.value === Role.TRAINER && auth.hasSomeRole(Role.ADMIN, Role.TRAINER, Role.VIEWER, Role.SCHOOL_MANAGER)) {
    await getTrainerModules();
  }

  if (role.value === Role.TRAINER && auth.hasRole(Role.ADMIN)) {
    await getTrainerSchools();
  }

  if (role.value === Role.SCHOOL_MANAGER && auth.hasSomeRole(Role.ADMIN, Role.VIEWER)) {
    await getSchoolAdminTrainers(route.params.id);
  } else if (!route.params.id && auth.hasRole(Role.SCHOOL_MANAGER)) {
    await getCurrentSchoolAdminTrainers();
  }

  if ([Role.TRAINER, Role.TRAINEE].includes(role.value) && (route.params.id || auth.hasRole(Role.TRAINEE))) {
    await getSchooling();
  }
});

async function fetchIsArchivedById() {
  if (id) {
    const response = await axios.get(`/account/${id}`);
    isArchived.value = response.data.isArchived
  }
}

const doShowInstitutionDiplomasTab = computed(() => {
  if (!auth.hasSomeRole(Role.ADMIN, Role.VIEWER, Role.SCHOOL_MANAGER, Role.TRAINER, Role.TRAINEE)) {
    return false;
  }

  return role.value === Role.TRAINEE;
});

const afterProfileFormSave = () => {
  if (accountHistoryLogsRef.value) {
    accountHistoryLogsRef.value.reset();
  }
};

async function getTrainerModules() {
  if (!route.params.id) {
    return;
  }

  const resp = await axios.get(`/training/profile-modules/${route.params.id}/`);
  modules.value = resp.data.map((item) => ({
    ...item,
    moduleName: getModuleName(item.translations),
    moduleType: getTypeName(item.type),
  }));
}

async function getTrainerSchools() {
  if (!route.params.id) {
    return;
  }

  const resp = await axios.get(`/school/schools/by-trainer/${route.params.id}/`);
  schools.value = resp.data;
}

const formatCreatedAt = (value) => {
  return `${dateFormat(value)} ${timeFormat(value)}`;
};

const getTypeName = (value /* key, item */) => {
  return t(value);
};

const getContentTypeName = (value /* key, item */) => {
  return value.type;
};

const getContentFullName = (value) => {
  return value.fullName || value.ssn;
};

const logFields = computed(() => {
  return [
    {key: "createdAt", label: t("TIME"), sortable: true, formatter: formatCreatedAt},
    {key: "createdBy", label: t("AUTHOR"), sortable: true, formatter: getContentFullName},
    {key: "type", label: t("TYPE"), sortable: true, formatter: getContentTypeName},
    {key: "message", label: t("CONTENT"), sortable: true},
    {key: "trainingGroup", label: t("GROUP"), sortable: true},
    {key: "training", label: t("MODULE"), sortable: true},
  ];
});

const attendanceFields = computed(() => {
  return [
    {
      key: "trainingGroup.trainingTranslation",
      label: t("MODULE"),
      formatter: getModuleName,
    },
    {key: "trainingGroup.title", label: t("GROUP")},
    {key: "trainingEvent.startTime", label: t("TIME"), sortable: true},
    {key: "status", label: t("STATUS"), sortable: true},
    {key: "hoursPresent", label: t("PRESENT"), sortable: true},
  ];
});

const hasRoleToViewGroup = () => {
  return auth.hasSomeRole(Role.ADMIN, Role.SCHOOL_MANAGER, Role.TRAINER, Role.VIEWER);
};

async function navToGroup(groupId) {
  const response = await axios.get(`/training/group/${groupId}/canViewGroup`);
  const canViewGroup = response.data.result;

  if (canViewGroup) {
    await router.push({name: "ManageGroup", params: {id: groupId}});
  } else {
    toast.makeErrorToast(t("ACCESS_DENIED"));
  }
}

const tabsList = computed(() => ([
  {key: "0", show: route.params.id && !auth.hasRole(Role.TRAINEE), title: t("LOGS")},
  {key: "1", show: trainings.value, title: t("SCHOOLING")},
  {key: "2", show: modules.value, title: t("MODULES")},
  {key: "3", show: schools.value, title: t("SCHOOLS")},
  {key: "4", show: role.value === Role.TRAINEE, title: t("ATTENDANCE")},
  {key: "5", show: adminTrainers.value, title: t("SCHOOL_TRAINERS")},
  {key: "6", show: doShowInstitutionDiplomasTab.value, title: t("OTHER_LANGUAGE_TRAINING")},
]));

const historyInfoSeverity = (item) => {
  if (item?.type === "DATA_UPDATE") return "success";
  if (item?.type === "GROUP_LEAVE") return "info";
  if (item?.type === "GROUP_REMOVE") return "warn";
  if (item?.type === "GROUP_REMOVE_OTHER") return "warn";
  if (item?.type === "GROUP_REMOVE_ELIGIBILITY") return "warn";
  if (item?.type === "ERROR") return "danger";
  return "secondary";
};

const badgeType = (item) => {
  if (!item) return "";
  if (item.status === "ARCHIVED") return "ended";
  if (
      item.status === "PUBLIC" &&
      moment(item.events[0].startTime).isAfter(moment())
  )
    return "upcoming";
  if (
      item.status === "PUBLIC" &&
      moment(item.events[item.events.length - 1].endTime).isBefore(moment())
  )
    return "ended";
  if (
      item.status === "PUBLIC" &&
      item.training.type === "RECURRING" &&
      moment(item.events[item.events.length - 1].endTime).isAfter(moment())
  )
    return "ongoing";
  return "";
};

async function getAccountHistoryLogs(params) {
  return await axios.get(`/account/history/${route.params.id}`, {params: params});
}

async function getSchooling() {
  let resp;

  if (route.params.id) {
    resp = await axios.get(`/account/groups/${route.params.id}`);
  } else {
    resp = await axios.get(`/account/groups/`);
  }

  trainings.value = resp.data.map((item) => ({
    ...item,
    startTime: item.events?.length ? dateFormat(item.events[0].startTime) : "",
    startTimeIso: item.events?.length ? dateFormatIso(item.events[0].startTime) : "",
    moduleName: item.training?.translations?.[locale.value],
    badgeType: badgeType(item),
  }));
}

async function getRole() {
  if (!route.params.id) {
    role.value = auth.getRole();
    return;
  }

  const resp = await axios.get(`/account/role/${route.params.id}`);
  role.value = resp.data;
}

async function getAttendance(params) {
  if (route.params.id) {
    return await axios.get(`/account/attendance/${route.params.id}`, {params: params});
  }

  return await axios.get(`/account/attendance/`, {params: params});
}

async function getSchoolAdminTrainers(schoolManagerId) {
  const resp = await axios.get(`/account/${schoolManagerId}/current_trainers`);
  adminTrainers.value = resp.data;
}

async function getCurrentSchoolAdminTrainers() {
  const resp = await axios.get(`/account/current_trainers`);
  adminTrainers.value = resp.data;
}

function getModuleName(value /* key, item */) {
  return value[locale.value];
}
</script>

<style scoped>
.main-container {
  padding-top: 2%;
}
</style>
